import { RUM_MAX_SELECTOR_LENGTH } from './constants'

import {
    clampToZero,
    getLoadState,
    getNavigationEntry,
    getResourceEntry,
    getSelector,
    toMs,
} from './performance-entries'
import type { CollectedMetric, RumAttribution } from './types'

interface LcpEntry extends PerformanceEntry {
    element?: Element | null
    url?: string
}

interface LayoutShiftSource {
    node?: Node | null
}

interface LayoutShiftEntry extends PerformanceEntry {
    value: number
    sources?: LayoutShiftSource[]
}

interface EventTimingEntry extends PerformanceEntry {
    processingStart: number
    processingEnd: number
    target?: Element | null
}

function truncate(value: string | undefined): string | undefined {
    if (!value) return undefined

    return value.slice(0, RUM_MAX_SELECTOR_LENGTH)
}

function lcpAttribution(metric: CollectedMetric): RumAttribution {
    const entry = metric.entries[metric.entries.length - 1] as LcpEntry | undefined
    const navigation = getNavigationEntry()
    const timeToFirstByte = clampToZero(navigation?.responseStart ?? 0)

    const resource = getResourceEntry(entry?.url)
    const requestStart =
        resource && resource.requestStart > 0 ? resource.requestStart : (resource?.startTime ?? timeToFirstByte)
    const responseEnd = resource && resource.responseEnd > 0 ? resource.responseEnd : requestStart

    return {
        target: getSelector(entry?.element),
        url: truncate(entry?.url),
        timeToFirstByte: toMs(timeToFirstByte),
        resourceLoadDelay: toMs(clampToZero(requestStart - timeToFirstByte)),
        resourceLoadDuration: toMs(clampToZero(responseEnd - requestStart)),
        elementRenderDelay: toMs(clampToZero(metric.value - responseEnd)),
    }
}

function inpAttribution(metric: CollectedMetric): RumAttribution {
    const events = metric.entries as EventTimingEntry[]
    const longest = events.reduce<EventTimingEntry | undefined>((worst, event) => {
        return !worst || event.duration > worst.duration ? event : worst
    }, undefined)

    if (!longest) return {}

    return {
        interactionTarget: getSelector(longest.target),
        interactionType: longest.name.startsWith('key') ? 'keyboard' : 'pointer',
        inputDelay: toMs(clampToZero(longest.processingStart - longest.startTime)),
        processingDuration: toMs(clampToZero(longest.processingEnd - longest.processingStart)),
        presentationDelay: toMs(clampToZero(longest.startTime + longest.duration - longest.processingEnd)),
        loadState: getLoadState(longest.startTime),
    }
}

function clsAttribution(metric: CollectedMetric): RumAttribution {
    const shifts = metric.entries as LayoutShiftEntry[]
    const largest = shifts.reduce<LayoutShiftEntry | undefined>((worst, shift) => {
        return !worst || shift.value > worst.value ? shift : worst
    }, undefined)

    if (!largest) return {}

    return {
        largestShiftTarget: getSelector(largest.sources?.[0]?.node),
        // A score, not milliseconds — rounding to an integer would zero it.
        largestShiftValue: Number(largest.value.toFixed(4)),
        loadState: getLoadState(largest.startTime),
    }
}

function fcpAttribution(metric: CollectedMetric): RumAttribution {
    const navigation = getNavigationEntry()
    const timeToFirstByte = clampToZero(navigation?.responseStart ?? 0)

    return {
        timeToFirstByte: toMs(timeToFirstByte),
        firstByteToFCP: toMs(clampToZero(metric.value - timeToFirstByte)),
        loadState: getLoadState(metric.entries[0]?.startTime ?? 0),
    }
}

/** Phases sum to the navigation entry's `responseStart`, i.e. the TTFB value. */
function ttfbAttribution(): RumAttribution {
    const navigation = getNavigationEntry()

    if (!navigation) return {}

    const workerStart = navigation.workerStart || navigation.fetchStart

    return {
        waitingDuration: toMs(clampToZero(workerStart)),
        cacheDuration: toMs(clampToZero(navigation.domainLookupStart - workerStart)),
        dnsDuration: toMs(clampToZero(navigation.domainLookupEnd - navigation.domainLookupStart)),
        connectionDuration: toMs(clampToZero(navigation.connectEnd - navigation.domainLookupEnd)),
        requestDuration: toMs(clampToZero(navigation.responseStart - navigation.connectEnd)),
    }
}

export function extractAttribution(metric: CollectedMetric): RumAttribution {
    try {
        return deriveAttribution(metric)
    } catch {
        // A metric with no attribution is still worth reporting.
        return {}
    }
}

function deriveAttribution(metric: CollectedMetric): RumAttribution {
    switch (metric.name) {
        case 'LCP':
            return lcpAttribution(metric)
        case 'INP':
            return inpAttribution(metric)
        case 'CLS':
            return clsAttribution(metric)
        case 'FCP':
            return fcpAttribution(metric)
        case 'TTFB':
            return ttfbAttribution()
        default:
            return {}
    }
}
